import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Users, DollarSign, Zap, Shield, Crosshair, AlertTriangle, Trophy, Send, ChevronRight, Hash, MapPin, MessageCircle, Sword, UserCircle, Crown, Settings } from 'lucide-react';
import NumbersGame from './NumbersGame';
import MultiplayerMap from './MultiplayerMap';
import CommunicationHub from './CommunicationHub';
import { WaterfrontTable, ChallengePanel, PlayerProfile } from './TableChallengeProfile';
import { HallOfFame, WaterfrontStatus } from './HallWaterfront';
import AdminDashboard from './AdminDashboard';
import RodriguezStoryMode from './RodriguezStoryMode';
import TaskForceView from './TaskForceView';
import CriminalCounterPlay from './CriminalCounterPlay';
import PvPRaidPanel from './PvPRaidPanel';
import EquipmentPanel from './EquipmentPanel';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// =============================================
// POLICE SCANNER — THE BADGE DASHBOARD
// =============================================
const PoliceScanner = ({ gameId, onBack, onStoryMode, onTaskForce, onCounterPlay, onPvPRaid, playerFaction }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bribing, setBribing] = useState(null);
  const [msg, setMsg] = useState(null);
  const [heatReduce, setHeatReduce] = useState(5);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/police/status/${gameId}`);
      if (res.ok) setStatus(await res.json());
    } catch (e) {}
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const payBribe = async (type, targetId, amount) => {
    setBribing(type); setMsg(null);
    try {
      const res = await fetch(`${API}/api/police/bribe/${gameId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bribe_type: type, target_id: targetId, amount }),
      });
      const d = await res.json();
      if (res.ok && d.success) { setMsg(d.narrator); load(); }
      else setMsg(d.detail || 'Failed');
    } catch (e) { setMsg('Network error'); }
    setBribing(null);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;
  if (!status) return null;

  const THREAT_COLORS = { LOW: '#4ADE80', MODERATE: '#C9A84C', HIGH: '#FF6B35', CRITICAL: '#DC2626' };
  const threatColor = THREAT_COLORS[status.threat_level] || '#E8E0D0';

  return (
    <div className="space-y-4" data-testid="police-scanner">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#1E90FF]" data-testid="badge-back">BACK</button>
      </div>

      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1E90FF]">THE BADGE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">COMMISSIONER RODRIGUEZ — NEW YORK CITY HQ</p>
        <p className="font-['Outfit'] text-[#E8E0D0]/12 text-[8px] tracking-wider mt-0.5">OPERATION LOW TIDE — INTERSTATE ORGANIZED CRIME TASK FORCE</p>
      </div>

      {/* Threat Level */}
      <div className="bg-[#111820] border rounded-xl p-4 text-center" style={{ borderColor: threatColor + '30' }}>
        <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30 tracking-widest mb-1">CURRENT THREAT LEVEL</p>
        <p className="font-['Cormorant_Garamond'] text-3xl font-bold" style={{ color: threatColor }}>{status.threat_level}</p>
        <p className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40 mt-1">
          Heat: {status.heat}/100 | Raid Chance: {status.raid_chance}% | Blocks Monitored: {status.territories_monitored}
        </p>
      </div>

      {/* Quota Progress — 8 Categories */}
      <div className="bg-[#111820] border border-[#1E90FF]/15 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-['Outfit'] text-[9px] text-[#1E90FF] tracking-widest">MONTHLY QUOTA</span>
          <span className={`font-['Outfit'] text-[8px] px-2 py-0.5 rounded ${
            status.quota.total_met >= status.quota.total_categories ? 'bg-[#4ADE80]/10 text-[#4ADE80]' : 'bg-[#DC2626]/10 text-[#DC2626]'
          }`}>{status.quota.total_met}/{status.quota.total_categories} MET</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {(status.quota.categories || []).map(cat => (
            <div key={cat.id} className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border ${
              cat.met ? 'bg-[#4ADE80]/5 border-[#4ADE80]/15' : 'bg-[#0a0c10] border-[#1A2535]'
            }`}>
              <span className={`font-['Outfit'] text-[8px] ${cat.met ? 'text-[#4ADE80]' : 'text-[#E8E0D0]/40'}`}>{cat.label}</span>
              <span className={`font-['Outfit'] text-[9px] font-bold ${cat.met ? 'text-[#4ADE80]' : 'text-[#E8E0D0]/60'}`}>{cat.current}/{cat.target}</span>
            </div>
          ))}
        </div>
        <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/15 text-center mt-2">
          Cycle resets in {status.quota.cycle_remaining_hours}h
        </p>
      </div>

      {/* Rodriguez Story Mode Entry */}
      <button onClick={() => onStoryMode && onStoryMode()}
        className="w-full bg-[#111820] border border-[#1E90FF]/20 rounded-xl p-4 text-left hover:border-[#1E90FF]/40 transition-all group"
        data-testid="badge-story-btn">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border border-[#1E90FF]/30">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/g4e6rkdn_1426B748-A180-4338-9F16-39392E5C61F1.png"
              alt="" className="w-full h-full object-cover" style={{ objectPosition: '50% 15%' }} />
          </div>
          <div className="flex-1">
            <h5 className="font-['Outfit'] text-sm text-[#1E90FF] font-bold group-hover:text-white transition-colors">THE COMMISSIONER — STORY MODE</h5>
            <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">The rise of Toribio Rodriguez. Every badge has a price.</p>
          </div>
          <ChevronRight size={16} className="text-[#1E90FF]/30 group-hover:text-[#1E90FF] transition-colors" />
        </div>
      </button>

      {/* Task Force / Interstate Network Entry */}
      <button onClick={() => onTaskForce && onTaskForce()}
        className="w-full bg-[#111820] border border-[#DC2626]/20 rounded-xl p-4 text-left hover:border-[#DC2626]/40 transition-all group"
        data-testid="badge-taskforce-btn">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#DC2626]/10 border border-[#DC2626]/20 flex items-center justify-center">
            <Shield size={18} className="text-[#DC2626]" />
          </div>
          <div className="flex-1">
            <h5 className="font-['Outfit'] text-sm text-[#DC2626] font-bold group-hover:text-white transition-colors">OPERATION LOW TIDE — TASK FORCES</h5>
            <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Atlanta (AMDOC) + Interstate Network. 8 states. ~48 officers.</p>
          </div>
          <ChevronRight size={16} className="text-[#DC2626]/30 group-hover:text-[#DC2626] transition-colors" />
        </div>
      </button>

      {/* Criminal Counter-Play — only for criminal faction players */}
      {playerFaction && playerFaction !== 'badge' && (
        <button onClick={() => onCounterPlay && onCounterPlay()}
          className="w-full bg-[#111820] border border-[#C9A84C]/20 rounded-xl p-4 text-left hover:border-[#C9A84C]/40 transition-all group"
          data-testid="badge-counterplay-btn">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
              <DollarSign size={18} className="text-[#C9A84C]" />
            </div>
            <div className="flex-1">
              <h5 className="font-['Outfit'] text-sm text-[#C9A84C] font-bold group-hover:text-white transition-colors">THE INSIDE MAN</h5>
              <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Bribe, flip, or blackmail compromised officers. Every cop has a price.</p>
            </div>
            <ChevronRight size={16} className="text-[#C9A84C]/30 group-hover:text-[#C9A84C] transition-colors" />
          </div>
        </button>
      )}

      {/* PvP Raids — Badge faction only */}
      {playerFaction === 'badge' && (
        <button onClick={() => onPvPRaid && onPvPRaid()}
          className="w-full bg-[#111820] border border-[#F59E0B]/20 rounded-xl p-4 text-left hover:border-[#F59E0B]/40 transition-all group"
          data-testid="badge-pvp-btn">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20 flex items-center justify-center">
              <Crosshair size={18} className="text-[#F59E0B]" />
            </div>
            <div className="flex-1">
              <h5 className="font-['Outfit'] text-sm text-[#F59E0B] font-bold group-hover:text-white transition-colors">PVP RAIDS — TARGET CRIMINALS</h5>
              <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/30">Execute real-time raids on criminal players. Seize cash, arrest crew, take territories.</p>
            </div>
            <ChevronRight size={16} className="text-[#F59E0B]/30 group-hover:text-[#F59E0B] transition-colors" />
          </div>
        </button>
      )}

      {/* Arrested Crew */}
      {status.arrested_count > 0 && (
        <div className="bg-[#111820] border border-[#DC2626]/15 rounded-xl p-4">
          <p className="font-['Outfit'] text-[9px] text-[#DC2626] tracking-widest mb-2">ARRESTED CREW ({status.arrested_count})</p>
          {Object.entries(status.arrested_crew).map(([crewId, data]) => (
            <div key={crewId} className="flex items-center justify-between py-2 border-b border-[#1A2535] last:border-0">
              <div>
                <span className="font-['Outfit'] text-xs text-[#E8E0D0]">{crewId}</span>
                <p className="font-['Outfit'] text-[8px] text-[#DC2626]/50">{data.offense} — {data.remaining_hours}h left</p>
              </div>
              <button onClick={() => payBribe('free_crew', crewId)} disabled={bribing === 'free_crew'}
                className="px-2 py-1 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded text-[#C9A84C] font-['Outfit'] text-[8px]"
                data-testid={`free-${crewId}`}>
                {bribing === 'free_crew' ? '...' : `BRIBE OUT ($${(status.bribe_costs.free_crew_base + Math.ceil(data.remaining_hours) * status.bribe_costs.free_crew_per_hour).toLocaleString()})`}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Raid Protection */}
      <div className="bg-[#111820] border border-[#1A2535] rounded-xl p-4">
        <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40 tracking-widest mb-2">PROTECTION & BRIBES</p>
        <div className="space-y-2">
          {/* Raid protection */}
          {status.raid_protection.active ? (
            <div className="flex items-center justify-between px-3 py-2 bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-lg">
              <span className="font-['Outfit'] text-[10px] text-[#4ADE80]">Raid Protection Active</span>
              <span className="font-['Outfit'] text-[8px] text-[#4ADE80]/50">{Math.ceil(status.raid_protection.remaining_seconds / 3600)}h left</span>
            </div>
          ) : (
            <button onClick={() => payBribe('raid_protection')} disabled={bribing === 'raid_protection'}
              className="w-full flex items-center justify-between px-3 py-2.5 border border-[#1E90FF]/20 rounded-lg hover:bg-[#1E90FF]/5 transition-colors"
              data-testid="buy-protection">
              <span className="font-['Outfit'] text-[10px] text-[#1E90FF]">Buy 24h Raid Protection</span>
              <span className="font-['Outfit'] text-xs text-[#C9A84C] font-bold">${status.bribe_costs.raid_protection_24h.toLocaleString()}</span>
            </button>
          )}

          {/* Reduce heat */}
          <div className="flex items-center gap-2">
            <input type="range" min="1" max={Math.max(1, status.heat)} value={heatReduce}
              onChange={e => setHeatReduce(parseInt(e.target.value))}
              className="flex-1 accent-[#1E90FF]" />
            <button onClick={() => payBribe('reduce_heat', null, heatReduce)} disabled={bribing === 'reduce_heat' || status.heat === 0}
              className="px-3 py-1.5 border border-[#C9A84C]/20 rounded-lg text-[#C9A84C] font-['Outfit'] text-[9px] hover:bg-[#C9A84C]/10 disabled:opacity-30"
              data-testid="reduce-heat">
              {bribing === 'reduce_heat' ? '...' : `BRIBE: -${heatReduce} HEAT ($${(heatReduce * status.bribe_costs.reduce_heat_per_point).toLocaleString()})`}
            </button>
          </div>
        </div>
      </div>

      {/* Raid History */}
      {status.raid_history.length > 0 && (
        <div className="bg-[#111820] border border-[#1A2535] rounded-xl p-4">
          <p className="font-['Outfit'] text-[9px] text-[#E8E0D0]/40 tracking-widest mb-2">RAID HISTORY</p>
          <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
            {status.raid_history.slice().reverse().map((raid, i) => (
              <div key={i} className="px-2 py-1.5 bg-[#0a0c10] rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/60">{raid.outcome_name}</span>
                  <span className={`font-['Outfit'] text-[7px] px-1.5 py-0.5 rounded ${
                    raid.severity === 'federal' ? 'bg-[#DC2626]/10 text-[#DC2626]' :
                    raid.severity === 'heavy' ? 'bg-[#FF6B35]/10 text-[#FF6B35]' :
                    raid.severity === 'medium' ? 'bg-[#C9A84C]/10 text-[#C9A84C]' :
                    'bg-[#E8E0D0]/5 text-[#E8E0D0]/30'
                  }`}>{raid.severity.toUpperCase()}</span>
                </div>
                <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/25 mt-0.5">{raid.narrative}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {msg && <p className="font-['Outfit'] text-[#C9A84C] text-xs text-center italic mt-2">"{msg}"</p>}
    </div>
  );
};

// =============================================
// DISPATCH MISSIONS
// =============================================
const DispatchPanel = ({ gameId, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectResults, setCollectResults] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/game/${gameId}/dispatch/available`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [gameId]);

  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);

  const handleStart = async (missionId) => {
    setStarting(missionId);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/dispatch/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mission_id: missionId }),
      });
      if (res.ok) { load(); onRefresh(); }
    } catch (e) { console.error(e); }
    setStarting(null);
  };

  const handleCollect = async () => {
    setCollecting(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/dispatch/collect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const results = await res.json();
        setCollectResults(results);
        setTimeout(() => setCollectResults(null), 5000);
        load();
        onRefresh();
      }
    } catch (e) { console.error(e); }
    setCollecting(false);
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-4">Loading...</p>;
  if (!data) return null;

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest">{data.available_crew_count}/{data.total_crew} CREW AVAILABLE</span>
      </div>

      {/* Collect results */}
      <AnimatePresence>
        {collectResults && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-1.5">
            {collectResults.results?.map((r, i) => (
              <div key={i} className={`p-2 rounded text-xs font-['Outfit'] ${r.success ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/20 text-[#4ADE80]' : 'bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 text-[#8B1A1A]'}`}>
                {r.success ? `${r.mission}: +$${r.cash?.toLocaleString()} +${r.xp}XP` : `${r.mission}: FAILED — ${r.desc}`}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active missions */}
      {data.active_missions?.length > 0 && (
        <div className="space-y-2">
          <span className="font-['Outfit'] text-[#C9A84C]/50 text-[9px] tracking-widest">IN PROGRESS</span>
          {data.active_missions.map(am => (
            <div key={am.mission_id} className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{am.mission_name}</h5>
                  <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">{am.crew_assigned?.length} crew deployed</p>
                </div>
                <div className="text-right">
                  <span className="font-['Outfit'] text-[#C9A84C] text-xs animate-pulse">{formatTime(am.remaining_seconds)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Finished missions — collect button */}
      {data.finished_missions?.length > 0 && (
        <button onClick={handleCollect} disabled={collecting}
          className="w-full px-4 py-3 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-lg text-[#4ADE80] font-['Outfit'] text-xs tracking-widest hover:bg-[#4ADE80]/20 disabled:opacity-30 transition-colors animate-pulse"
          data-testid="dispatch-collect-btn">
          {collecting ? 'COLLECTING...' : `COLLECT ${data.finished_missions.length} DISPATCH RESULT${data.finished_missions.length > 1 ? 'S' : ''}`}
        </button>
      )}

      {/* Available missions */}
      <div className="space-y-2">
        {data.missions?.map(m => (
          <div key={m.id} className="bg-[#111820] border border-[#1A2535] rounded-lg p-3 hover:border-[#C9A84C]/20 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{m.name}</h5>
                <div className="flex items-center gap-3 mt-1">
                  <span className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px]"><Clock size={9} className="inline mr-0.5" />{m.hours}h</span>
                  <span className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px]"><Users size={9} className="inline mr-0.5" />{m.crew_needed} crew</span>
                  <span className="font-['Outfit'] text-[#4ADE80]/40 text-[9px]">${m.reward_range?.cash[0]?.toLocaleString()}-${m.reward_range?.cash[1]?.toLocaleString()}</span>
                  <span className="font-['Outfit'] text-[#8B1A1A]/40 text-[9px]">{Math.round(m.risk * 100)}% risk</span>
                </div>
              </div>
              <button onClick={() => handleStart(m.id)} disabled={!m.can_start || starting === m.id}
                className="px-3 py-1.5 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded text-[#C9A84C] font-['Outfit'] text-[9px] tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-20 disabled:cursor-not-allowed transition-colors shrink-0"
                data-testid={`dispatch-start-${m.id}`}>
                {starting === m.id ? '...' : 'DEPLOY'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================
// THE WIRE
// =============================================
const WirePanel = ({ gameId, onRefresh }) => {
  const [wire, setWire] = useState(null);
  const [nextWave, setNextWave] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const startWire = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/wire/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setWire(data.wire);
        setNextWave(data.wave);
      } else {
        const err = await res.json();
        setResult({ error: err.detail });
        setTimeout(() => setResult(null), 3000);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fightWave = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/wire/fight`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setWire(data.wire);
        setNextWave(data.next_wave || null);
        setResult(data);
        if (data.busted || data.completed) {
          onRefresh();
          setTimeout(() => { setWire(null); setResult(null); }, 4000);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const cashOut = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/wire/cashout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setResult({ cashout: true, ...data });
        onRefresh();
        setTimeout(() => { setWire(null); setResult(null); }, 3000);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (!wire) {
    return (
      <div className="text-center py-6">
        <Crosshair size={32} className="text-[#8B1A1A]/30 mx-auto mb-3" />
        <h5 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0] mb-1">THE WIRE</h5>
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-1">Survive 7 waves of escalating raids.</p>
        <p className="font-['Outfit'] text-[#8B1A1A]/50 text-[10px] mb-4">Get busted = lose ALL accumulated rewards. Cash out anytime.</p>
        {result?.error && <p className="font-['Outfit'] text-[#8B1A1A] text-xs mb-3">{result.error}</p>}
        <button onClick={startWire} disabled={loading}
          className="px-6 py-2 bg-[#8B1A1A]/10 border border-[#8B1A1A]/40 rounded text-[#8B1A1A] font-['Outfit'] text-xs tracking-widest hover:bg-[#8B1A1A]/20 disabled:opacity-30"
          data-testid="wire-start-btn">
          {loading ? '...' : 'ENTER THE WIRE (10 ENERGY)'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Current state */}
      <div className="bg-[#111820] border border-[#8B1A1A]/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-['Outfit'] text-[#8B1A1A] text-[10px] tracking-widest">WAVE {wire.current_wave}/7</span>
          <span className="font-['Outfit'] text-[#4ADE80] text-xs">${wire.accumulated_cash?.toLocaleString()} banked</span>
        </div>
        {nextWave && (
          <h5 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0] mb-1">{nextWave.name}</h5>
        )}
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">
          Difficulty: {nextWave?.difficulty || '?'} | Reward: ${nextWave?.reward_cash?.toLocaleString() || '?'}
        </p>
      </div>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className={`p-3 rounded-lg text-center ${result.busted ? 'bg-[#8B1A1A]/15 border border-[#8B1A1A]/30' : result.cashout ? 'bg-[#4ADE80]/10 border border-[#4ADE80]/30' : result.completed ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/30' : 'bg-[#4ADE80]/10 border border-[#4ADE80]/30'}`}>
            <p className="font-['Outfit'] text-xs" style={{ color: result.busted ? '#8B1A1A' : '#4ADE80' }}>
              {result.narrator || result.cashout && `Cashed out! +$${result.cash?.toLocaleString()}`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      {wire.alive && (
        <div className="flex gap-2">
          <button onClick={fightWave} disabled={loading}
            className="flex-1 px-4 py-2.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/40 rounded text-[#8B1A1A] font-['Outfit'] text-xs tracking-widest hover:bg-[#8B1A1A]/20 disabled:opacity-30"
            data-testid="wire-fight-btn">
            {loading ? '...' : 'FIGHT WAVE'}
          </button>
          <button onClick={cashOut} disabled={loading || wire.accumulated_cash === 0}
            className="flex-1 px-4 py-2.5 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded text-[#4ADE80] font-['Outfit'] text-xs tracking-widest hover:bg-[#4ADE80]/20 disabled:opacity-30"
            data-testid="wire-cashout-btn">
            {loading ? '...' : 'CASH OUT'}
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================
// BOSS RUSH
// =============================================
const BossRushPanel = ({ gameId, onRefresh }) => {
  const [rush, setRush] = useState(null);
  const [nextBoss, setNextBoss] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const startRush = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/bossrush/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setRush(data.rush);
        setNextBoss(data.next_boss);
      } else {
        const err = await res.json();
        setResult({ error: err.detail });
        setTimeout(() => setResult(null), 3000);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fightBoss = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/game/${gameId}/bossrush/fight`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setRush(data.rush || null);
        setNextBoss(data.next_boss || null);
        setResult(data);
        if (data.completed || !data.success) {
          onRefresh();
          setTimeout(() => { setRush(null); setResult(null); }, 4000);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (!rush) {
    return (
      <div className="text-center py-6">
        <Trophy size={32} className="text-[#C9A84C]/30 mx-auto mb-3" />
        <h5 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0] mb-1">BOSS RUSH</h5>
        <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs mb-1">Fight all 4 era bosses back-to-back.</p>
        <p className="font-['Outfit'] text-[#C9A84C]/50 text-[10px] mb-4">Damage carries over. Die = lose everything. Weekly reset.</p>
        {result?.error && <p className="font-['Outfit'] text-[#8B1A1A] text-xs mb-3">{result.error}</p>}
        <button onClick={startRush} disabled={loading}
          className="px-6 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30"
          data-testid="bossrush-start-btn">
          {loading ? '...' : 'BEGIN BOSS RUSH (20 ENERGY)'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-['Outfit'] text-[#C9A84C] text-[10px] tracking-widest">BOSS {rush.current_boss + 1}/4</span>
          <span className="font-['Outfit'] text-[#8B1A1A] text-xs">HP: {rush.player_hp}</span>
        </div>
        {nextBoss && (
          <>
            <h5 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0] mb-1">{nextBoss.name}</h5>
            <div className="flex gap-3 text-[9px] font-['Outfit'] text-[#E8E0D0]/30">
              <span>HP: {nextBoss.hp}</span>
              <span>ATK: {nextBoss.attack}</span>
              <span>DEF: {nextBoss.defense}</span>
              <span className="text-[#4ADE80]/50">+${nextBoss.reward_cash?.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            className={`p-3 rounded-lg text-center ${!result.success ? 'bg-[#8B1A1A]/15 border border-[#8B1A1A]/30' : result.completed ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/30' : 'bg-[#4ADE80]/10 border border-[#4ADE80]/30'}`}>
            <p className="font-['Outfit'] text-xs" style={{ color: !result.success ? '#8B1A1A' : '#4ADE80' }}>
              {result.completed ? `ALL BOSSES DEFEATED! +$${result.rush?.accumulated_cash?.toLocaleString()}` :
               !result.success ? `Defeated by ${result.defeated_by}. Lost all rewards.` :
               `${result.boss_defeated} DEFEATED! HP: ${result.player_hp}`}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {rush && !result?.completed && result?.success !== false && (
        <button onClick={fightBoss} disabled={loading}
          className="w-full px-4 py-2.5 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30"
          data-testid="bossrush-fight-btn">
          {loading ? 'FIGHTING...' : `FIGHT ${nextBoss?.name || 'BOSS'}`}
        </button>
      )}
    </div>
  );
};

// =============================================
// MODE CARD IMAGES
// =============================================
const MODE_IMAGES = {
  territory: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/7bad24792ca9ac03cbcd76fa0c5a0478d0233a23b8fad4554cf4975e26450edb.jpeg',
  table: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/1c20a47a6d093a957cd67ed8a48081de3adac96c28452c64605801e4cc1b6871.jpeg',
  challenge: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/968213e81a33ed125537c5b681d0e25b44ad148ca2f6225a0165611df831a0f9.jpeg',
  profile: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/08edc59a2dd5159ebf9ce8dec1fe91294ba9ce984cfd742b75f8dc255d55e4ec.jpeg',
  comms: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/c9b60322ba15e42ff5db84d6bcd5660a9e64bb82bda163bdb102e64e6206fb98.jpeg',
  numbers: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/652368794010184df302c0b977cff6b51b3ec8b0cbb8d76187e4c3a45be613d8.jpeg',
  dispatch: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/d911de84628eb69f142344c9bafcc8a878a72df81d67fe23a5a9a636304d298d.jpeg',
  wire: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/0437673853d66ac261fc84c6a5495e7c433ce3deac6b22a123e5cf055ce4ee3a.jpeg',
  bossrush: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/6dabb73ddec2b3306251f45e8f1d2390c76227856d58ae322f3430ae1afd052a.jpeg',
  waterfront: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/625657315a4064903c18f041c6e3122b4afe5a75d7f25a91ebee66f24e143e2d.jpeg',
  hallfame: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/089828c4b15d7ac15a8eb3225420fa0d1de08d6e267f5d2d3530082214489239.jpeg',
  admin: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/69ad5e375d92cb07c84b730a6f7eb2c5f7b1e737608ea0a7ad04a0b4598e4280.jpeg',
  badge: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/0437673853d66ac261fc84c6a5495e7c433ce3deac6b22a123e5cf055ce4ee3a.jpeg',
  loadout: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/69ad5e375d92cb07c84b730a6f7eb2c5f7b1e737608ea0a7ad04a0b4598e4280.jpeg',
};

// Coming soon modes — these don't have full backends yet
const COMING_SOON = new Set(['hallfame']);

// =============================================
// CINEMATIC MODE CARD
// =============================================
const ModeCard = ({ mode, onClick, isLarge }) => {
  const isLocked = COMING_SOON.has(mode.id);
  return (
    <motion.button
      whileHover={isLocked ? {} : { scale: 1.02 }}
      whileTap={isLocked ? {} : { scale: 0.98 }}
      onClick={isLocked ? undefined : onClick}
      className={`relative w-full overflow-hidden rounded-xl text-left transition-all group ${
        isLocked ? 'cursor-default opacity-70' : 'cursor-pointer'
      }`}
      style={{ height: isLarge ? '180px' : '140px' }}
      data-testid={`mode-${mode.id}`}
    >
      {/* Background image */}
      <img
        src={MODE_IMAGES[mode.id]}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ${
          isLocked ? 'grayscale-[60%]' : 'group-hover:scale-110'
        }`}
        loading="lazy"
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/20" />
      {/* Accent border on hover */}
      <div className="absolute inset-0 rounded-xl border border-transparent transition-all duration-300"
        style={{ borderColor: isLocked ? 'transparent' : undefined }}
        onMouseEnter={e => { if (!isLocked) e.currentTarget.style.borderColor = mode.color + '50'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
      />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center backdrop-blur-sm"
            style={{ background: mode.color + '20', border: `1px solid ${mode.color}30` }}>
            <span style={{ color: mode.color }}>{mode.icon}</span>
          </div>
          <div className="flex-1">
            <h5 className={`font-['Outfit'] text-sm font-bold tracking-wider transition-colors ${
              isLocked ? 'text-[#E8E0D0]/50' : 'text-[#E8E0D0] group-hover:text-white'
            }`}>{mode.name}</h5>
          </div>
          {isLocked ? (
            <span className="font-['Outfit'] text-[8px] tracking-[0.2em] px-2 py-1 rounded-full bg-[#E8E0D0]/5 border border-[#E8E0D0]/15 text-[#E8E0D0]/40">
              COMING SOON
            </span>
          ) : (
            <ChevronRight size={16} className="text-[#E8E0D0]/20 group-hover:text-[#C9A84C] transition-colors" />
          )}
        </div>
        <p className={`font-['Outfit'] text-[10px] leading-relaxed ${
          isLocked ? 'text-[#E8E0D0]/25' : 'text-[#E8E0D0]/45'
        }`}>{mode.desc}</p>
      </div>

      {/* Locked overlay */}
      {isLocked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-[#050505]/60 backdrop-blur-sm border border-[#E8E0D0]/10 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8E0D040" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        </div>
      )}
    </motion.button>
  );
};

// =============================================
// MAIN GAME MODES TAB
// =============================================
const GameModesTab = ({ gameId, onRefresh, username, faction }) => {
  const [activeMode, setActiveMode] = useState(null);

  const modes = [
    { id: 'territory', name: 'TERRITORY WAR', icon: <MapPin size={16} />, desc: '228 blocks across NYC. Claim. Attack. Defend. The map never sleeps.', color: '#FFD700' },
    { id: 'table', name: 'THE TABLE', icon: <Users size={16} />, desc: 'Social deduction. Hidden roles. Betray or be betrayed. 2-8 players.', color: '#9B59B6' },
    { id: 'challenge', name: '1v1 CHALLENGE', icon: <Sword size={16} />, desc: 'Attack. Defend. Bluff. Best of 3. Cash or territory on the line.', color: '#8B1A1A' },
    { id: 'profile', name: 'MY PROFILE', icon: <UserCircle size={16} />, desc: 'Your stats, badges, territory, combat record. Your legacy.', color: '#4A7AB5' },
    { id: 'comms', name: 'COMMS', icon: <MessageCircle size={16} />, desc: 'Global chat. Crew channels. Wars. Bounties. The streets are talking.', color: '#4A7AB5' },
    { id: 'numbers', name: 'THE NUMBERS', icon: <Hash size={16} />, desc: 'Play the numbers. Pick 3 digits. Pray the runner makes it.', color: '#4ADE80' },
    { id: 'dispatch', name: 'DISPATCH', icon: <Send size={16} />, desc: 'Send crew on timed auto-missions. Set it and forget it.', color: '#C9A84C' },
    { id: 'wire', name: 'THE WIRE', icon: <Shield size={16} />, desc: 'Survive 7 waves of raids. Cash out or bust.', color: '#8B1A1A' },
    { id: 'bossrush', name: 'BOSS RUSH', icon: <Trophy size={16} />, desc: 'Fight all 4 era bosses back-to-back. Daily reset.', color: '#C9A84C' },
    { id: 'waterfront', name: 'WATERFRONT', icon: <Crown size={16} />, desc: 'Premium district. Harbor Crown. Income collection. Endgame.', color: '#FFD700' },
    { id: 'hallfame', name: 'HALL OF FAME', icon: <Trophy size={16} />, desc: 'Permanent records. Legends. The water remembers.', color: '#FFD700' },
    { id: 'loadout', name: 'LOADOUT', icon: <Sword size={16} />, desc: 'Equip weapons and vehicles. Boost job success and boss fights.', color: '#C9A84C' },
    { id: 'admin', name: 'ADMIN', icon: <Settings size={16} />, desc: 'Dashboard. Players. Territory. Anti-cheat flags.', color: '#E8E0D0' },
    { id: 'badge', name: 'THE BADGE', icon: <Shield size={16} />, desc: 'Commissioner Rodriguez. Raids. Arrests. Quotas. The law never sleeps.', color: '#1E90FF' },
  ];

  // --- Active mode routing (unchanged) ---
  if (activeMode === 'territory') {
    return <MultiplayerMap gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'comms') {
    return <CommunicationHub gameId={gameId} username={username || 'Unknown'} faction={faction} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'table') {
    return <WaterfrontTable gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'challenge') {
    return <ChallengePanel gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'profile') {
    return <PlayerProfile gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'waterfront') {
    return <WaterfrontStatus gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'hallfame') {
    return <HallOfFame onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'loadout') {
    return <EquipmentPanel gameId={gameId} onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'admin') {
    return <AdminDashboard onBack={() => setActiveMode(null)} />;
  }

  if (activeMode === 'badge') {
    return <PoliceScanner gameId={gameId} playerFaction={faction} onBack={() => setActiveMode(null)} onStoryMode={() => setActiveMode('badge_story')} onTaskForce={() => setActiveMode('badge_taskforce')} onCounterPlay={() => setActiveMode('badge_counterplay')} onPvPRaid={() => setActiveMode('badge_pvp')} />;
  }

  if (activeMode === 'badge_story') {
    return <RodriguezStoryMode gameId={gameId} onBack={() => setActiveMode('badge')} />;
  }

  if (activeMode === 'badge_taskforce') {
    return <TaskForceView onBack={() => setActiveMode('badge')} />;
  }

  if (activeMode === 'badge_counterplay') {
    return <CriminalCounterPlay gameId={gameId} onBack={() => setActiveMode('badge')} />;
  }

  if (activeMode === 'badge_pvp') {
    return <PvPRaidPanel gameId={gameId} onBack={() => setActiveMode('badge')} onRefreshGame={onRefresh} />;
  }

  if (activeMode) {
    return (
      <div>
        <button onClick={() => setActiveMode(null)}
          className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#C9A84C] transition-colors mb-3"
          data-testid="modes-back-btn">
          BACK TO MODES
        </button>
        {activeMode === 'numbers' && <NumbersGame gameId={gameId} onRefresh={onRefresh} />}
        {activeMode === 'dispatch' && <DispatchPanel gameId={gameId} onRefresh={onRefresh} />}
        {activeMode === 'wire' && <WirePanel gameId={gameId} onRefresh={onRefresh} />}
        {activeMode === 'bossrush' && <BossRushPanel gameId={gameId} onRefresh={onRefresh} />}
      </div>
    );
  }

  // --- Featured modes (top 2 large) ---
  const featured = modes.filter(m => m.id === 'territory' || m.id === 'wire');
  const combat = modes.filter(m => ['challenge', 'bossrush', 'table'].includes(m.id));
  const hustle = modes.filter(m => ['numbers', 'dispatch', 'waterfront'].includes(m.id));
  const social = modes.filter(m => ['profile', 'comms', 'hallfame'].includes(m.id));
  const system = modes.filter(m => ['admin', 'loadout'].includes(m.id));
  const police = modes.filter(m => m.id === 'badge');

  return (
    <div className="space-y-5" data-testid="modes-grid">
      {/* Section header */}
      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C]">GAME MODES</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[10px] tracking-widest mt-1">CHOOSE YOUR BATTLEGROUND</p>
      </div>

      {/* Featured — large cards */}
      <div>
        <p className="font-['Outfit'] text-[#8B1A1A] text-[9px] tracking-[0.3em] mb-2">FEATURED</p>
        <div className="grid grid-cols-2 gap-3">
          {featured.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} isLarge />
          ))}
        </div>
      </div>

      {/* Combat modes */}
      <div>
        <p className="font-['Outfit'] text-[#8B1A1A] text-[9px] tracking-[0.3em] mb-2">COMBAT</p>
        <div className="grid grid-cols-3 gap-3">
          {combat.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} />
          ))}
        </div>
      </div>

      {/* Hustle modes */}
      <div>
        <p className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-[0.3em] mb-2">THE HUSTLE</p>
        <div className="grid grid-cols-3 gap-3">
          {hustle.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} />
          ))}
        </div>
      </div>

      {/* Social / Intel */}
      <div>
        <p className="font-['Outfit'] text-[#4A7AB5] text-[9px] tracking-[0.3em] mb-2">INTEL</p>
        <div className="grid grid-cols-3 gap-3">
          {social.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} />
          ))}
        </div>
      </div>

      {/* The Badge — Police */}
      <div>
        <p className="font-['Outfit'] text-[#1E90FF] text-[9px] tracking-[0.3em] mb-2">THE LAW</p>
        <div className="grid grid-cols-2 gap-3">
          {police.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} isLarge />
          ))}
        </div>
      </div>

      {/* Admin — system */}
      <div>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-[0.3em] mb-2">SYSTEM</p>
        <div className="grid grid-cols-3 gap-3">
          {system.map(m => (
            <ModeCard key={m.id} mode={m} onClick={() => setActiveMode(m.id)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GameModesTab;
